import {
  EQUIPMENT_SLOT_LAYOUT as SHARED_SLOT_LAYOUT,
  EQUIPMENT_SLOT_GROUPS,
  RETIRED_EQUIPMENT_SLOT_KEYS,
} from '../../../shared/rules/equipmentSlots.mjs';

// Slot keys, labels, groups and retired slots come from the shared list; this file only adds
// which inventory items the Equipment screen offers for each slot.
const SLOT_ITEM_FILTERS = {
  head: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: [
          'head',
          'helm',
          'helmet',
          'hat',
          'circlet',
          'crown',
          'headband',
        ],
      },
      armor: {
        categories: ['helm', 'helmet', 'head'],
      },
      accessory: {
        categories: [
          'head',
          'helm',
          'helmet',
          'hat',
          'circlet',
          'crown',
          'headband',
        ],
        targetSlots: ['head'],
      },
    },
  },
  eyes: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['eye', 'eyes', 'goggles', 'lens', 'visor', 'glasses'],
      },
      accessory: {
        categories: ['eye', 'eyes', 'goggles', 'lens', 'visor', 'glasses'],
        targetSlots: ['eyes'],
      },
    },
  },
  neck: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['neck', 'amulet', 'necklace', 'pendant', 'torc'],
      },
      accessory: {
        categories: ['neck', 'amulet', 'necklace', 'pendant', 'torc'],
        targetSlots: ['neck'],
      },
    },
  },
  shoulders: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: [
          'shoulder',
          'shoulders',
          'pauldron',
          'spauldron',
          'epaulet',
          'epaulette',
          'mantle',
        ],
      },
      armor: {
        categories: [
          'shoulder',
          'shoulders',
          'pauldron',
          'spauldron',
          'epaulet',
          'epaulette',
          'mantle',
        ],
      },
      accessory: {
        categories: [
          'shoulder',
          'shoulders',
          'pauldron',
          'spauldron',
          'epaulet',
          'epaulette',
          'mantle',
        ],
        targetSlots: ['shoulders'],
      },
    },
  },
  chest: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      // One suit of armor; shields go in the off hand.
      armor: {
        categories: ['light', 'medium', 'heavy', 'chest', 'mail', 'plate'],
      },
      item: {
        categories: ['armor', 'chest', 'vest', 'robe', 'mail', 'shirt', 'plate'],
      },
      accessory: {
        categories: ['armor', 'chest', 'vest', 'robe', 'mail', 'shirt', 'plate'],
        targetSlots: ['chest'],
      },
    },
  },
  back: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: ['back', 'cloak', 'cape', 'mantle'],
      },
      armor: {
        categories: ['back', 'cloak', 'cape', 'mantle'],
      },
      accessory: {
        categories: ['back', 'cloak', 'cape', 'mantle', 'wings'],
        targetSlots: ['back'],
      },
    },
  },
  arms: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: ['arm', 'arms', 'bracer', 'sleeve'],
      },
      armor: {
        categories: ['arm', 'arms', 'bracer', 'vambrace'],
      },
      accessory: {
        categories: ['arm', 'arms', 'bracer', 'sleeve', 'vambrace'],
        targetSlots: ['arms'],
      },
    },
  },
  wrists: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['wrist', 'bracelet', 'bracer', 'cuff'],
      },
      accessory: {
        categories: ['wrist', 'bracelet', 'bracer', 'cuff'],
        targetSlots: ['wrists'],
      },
    },
  },
  hands: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: ['hand', 'hands', 'glove', 'gauntlet', 'mitt'],
      },
      armor: {
        categories: ['hand', 'hands', 'glove', 'gauntlet', 'mitt'],
      },
      accessory: {
        categories: ['hand', 'hands', 'glove', 'gauntlet', 'mitt'],
        targetSlots: ['hands'],
      },
    },
  },
  waist: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['belt', 'waist', 'sash', 'girdle'],
      },
      accessory: {
        categories: ['belt', 'waist', 'sash', 'girdle'],
        targetSlots: ['waist'],
      },
    },
  },
  legs: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: ['leg', 'legs', 'greaves', 'leggings', 'pants', 'skirt'],
      },
      armor: {
        categories: ['leg', 'legs', 'greaves', 'leggings', 'pants', 'skirt'],
      },
      accessory: {
        categories: ['leg', 'legs', 'greaves', 'leggings', 'pants', 'skirt'],
        targetSlots: ['legs'],
      },
    },
  },
  feet: {
    allowedSources: ['armor', 'item', 'accessory'],
    filters: {
      item: {
        categories: ['feet', 'foot', 'boot', 'boots', 'shoe', 'sandals', 'slippers'],
      },
      armor: {
        categories: ['feet', 'foot', 'boot', 'boots', 'shoe', 'sandals', 'slippers'],
      },
      accessory: {
        categories: ['feet', 'foot', 'boot', 'boots', 'shoe', 'sandals', 'slippers'],
        targetSlots: ['feet'],
      },
    },
  },
  mainHand: {
    allowedSources: ['weapon'],
  },
  offHand: {
    excludeTwoHanded: true,
    allowedSources: ['weapon', 'armor'],
    filters: {
      armor: {
        categories: ['shield'],
      },
    },
  },
  ranged: {
    allowedSources: ['weapon'],
    filters: {
      weapon: {
        categories: ['ranged', 'bow', 'crossbow', 'thrown', 'gun'],
      },
    },
  },
  ringLeft: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['ring', 'band', 'signet'],
      },
      accessory: {
        categories: ['ring', 'band', 'signet'],
        targetSlots: ['ring', 'ringleft'],
      },
    },
  },
  ringRight: {
    allowedSources: ['item', 'accessory'],
    filters: {
      item: {
        categories: ['ring', 'band', 'signet'],
      },
      accessory: {
        categories: ['ring', 'band', 'signet'],
        targetSlots: ['ring', 'ringright'],
      },
    },
  },
};

// Extra worn slots offer the same items as the first slot of their kind (2024 lets these stack).
SLOT_ITEM_FILTERS.neck2 = SLOT_ITEM_FILTERS.neck;
SLOT_ITEM_FILTERS.neck3 = SLOT_ITEM_FILTERS.neck;
SLOT_ITEM_FILTERS.eyes2 = SLOT_ITEM_FILTERS.eyes;
SLOT_ITEM_FILTERS.waist2 = SLOT_ITEM_FILTERS.waist;
const anyRing = {
  allowedSources: ['item', 'accessory'],
  filters: {
    item: { categories: ['ring', 'band', 'signet'] },
    accessory: { categories: ['ring', 'band', 'signet'], targetSlots: ['ring', 'ringleft', 'ringright'] },
  },
};
SLOT_ITEM_FILTERS.ring3 = anyRing;
SLOT_ITEM_FILTERS.ring4 = anyRing;

const createSlot = ({ key, label, retired }) => {
  const config = SLOT_ITEM_FILTERS[key] || {};
  return {
    key,
    label,
    allowedSources: config.allowedSources,
    filters: config.filters || {},
    retired: Boolean(retired),
    excludeTwoHanded: Boolean(config.excludeTwoHanded),
  };
};

export const EQUIPMENT_SLOT_LAYOUT = SHARED_SLOT_LAYOUT.map((row) => row.map(createSlot));

export const EQUIPMENT_SLOT_KEYS = EQUIPMENT_SLOT_LAYOUT.flat().map((slot) => slot.key);

export { EQUIPMENT_SLOT_GROUPS, RETIRED_EQUIPMENT_SLOT_KEYS };
